import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import ImageItem from '../../../components/ImageItem';
import {
  Grid,
  makeStyles,
  Box,
  Typography,
  IconButton
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import Lightbox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import CardInfoImage from './CardInfoImage';
import { Typography as AntTypography } from 'antd';
import { useNavigate } from 'react-router';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip,
  useMap
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LicensePlateTag from 'src/app/components/licensePlateTag/LicesePlateTag';
import { getIconStatus } from 'src/app/utils/vehicleService';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import moment from 'moment';


const DEFAULT_CENTER = [21.024683, 105.832495];
const TOOLTIP_OFFSET = [0, -35];
const POPUP_OFFSET = [0, -35];
const THUMBNAIL_ITEM_WIDTH = 270;
const THUMBNAIL_ITEM_HEIGHT = 184;
const ASPECT_RATIO = 16 / 9;

const MapUpdater = ({ isSidebarOpen, center, marker, location }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
    setTimeout(() => {
      map.invalidateSize();
    }, 300);
  }, [isSidebarOpen, center, map]);

  return marker ? (
    <Marker
      position={center}
      icon={marker.icon}
      rotationAngle={marker.rotation || 0}
      rotationOrigin="center"
    >
      <Tooltip
        direction="center"
        offset={TOOLTIP_OFFSET}
        autoPan={true}
        opacity={1}
        permanent={true}
      >
        <Box style={{ marginTop: '0px' }}>
          <LicensePlateTag
            isSelected={true}
            position={{ lat: center[0], lng: center[1] }}
            license_plate={marker.license_plate}
            color={'white'}
            backgroundColor={'#D9042F'}
          />
        </Box>
      </Tooltip>
      <Popup offset={POPUP_OFFSET}>
        <Box
          style={{
            textAlign: 'center',
            padding: '5px',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <Typography
            style={{
              fontSize: '15px',
              fontWeight: 'bold',
              margin: 0,
              padding: 0
            }}
          >
            Địa chỉ:{' '}
            <span style={{ color: '#666666', fontSize: '13px',
              fontWeight: '500', }}>
              {location || 'Không có thông tin địa chỉ'}
            </span>
          </Typography>
        </Box>
      </Popup>
    </Marker>
  ) : null;
};

const ThumbnailItem = React.memo(
  ({
    image,
    index,
    selectedImage,
    classes,
    handleThumbnailClick,
    handleDownload,
    style
  }) => {
    return (
      <Box
        className={`${classes.thumbnail} ${
          selectedImage?.id === image.id ? classes.thumbnailSelected : ''
        }`}
        onClick={() => handleThumbnailClick(image, index)}
        style={style}
      >
        <img
          src={image.thumbnailUrl}
          alt={image.file_name}
          className={classes.thumbnailImage}
          loading="lazy"
        />
        <Box className={classes.thumbnailInfo}>
          <Box className={classes.leftSection}>
            <Box className={classes.thumbnailInfoItem}>
              <img
                src="/static/iconSvg/iconTime.svg"
                alt="Time Icon"
                className={classes.thumbnailIcon}
              />
              <Typography
                variant="caption"
                className={classes.typographyCaption}
              >
                {moment(image.created_at * 1000).format(
                                          'HH:mm:ss DD/MM/YYYY'
                                        ) || '--'}
              </Typography>
            </Box>
            <Box className={classes.thumbnailInfoItem}>
              <img
                src="/static/iconSvg/iconLocationRed.svg"
                alt="Location Icon"
                className={classes.thumbnailIcon}
              />
              <Typography
                variant="caption"
                className={classes.typographyCaption}
              >
                {image.device.location}
              </Typography>
            </Box>
            <Box className={classes.thumbnailInfoItem}>
              <img
                src="/static/iconSvg/iconCamera.svg"
                alt="Camera Icon"
                className={classes.thumbnailIcon}
              />
              <Typography
                variant="caption"
                className={classes.typographyCaption}
              >
                {image.camera}
              </Typography>
            </Box>
          </Box>
          <Box className={classes.rightSection}>
            <Box className={classes.thumbnailInfoItem}>
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  handleDownload(image.url);
                }}
                className={classes.downloadButton}
              >
                <img
                  src="/static/iconSvg/iconDownloadRed.svg"
                  alt="Download Icon"
                  className={classes.downloadIcon}
                />
              </IconButton>
            </Box>
            <Box className={classes.thumbnailInfoItem}>
              <Typography
                variant="caption"
                className={classes.typographyCaption}
              >
                {image.device.vehicle.license_plate}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

ThumbnailItem.propTypes = {
  image: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  selectedImage: PropTypes.object,
  classes: PropTypes.object.isRequired,
  handleThumbnailClick: PropTypes.func.isRequired,
  handleDownload: PropTypes.func.isRequired,
  style: PropTypes.object
};

const useStyles = makeStyles(theme => ({
  container: {
    position: 'absolute',
    top: '66px',
    left: props => (props.isSidebarOpen ? '384px' : '28px'),
    right: 0,
    bottom: 0,
    width: 'auto',
    height: 'calc(100vh - 66px)',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    margin: 0,
    padding: 0,
    zIndex: 10,
    boxSizing: 'border-box'
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'row',
    height: 'calc(100vh - 66px - 200px)',
    width: '100%',
    flexShrink: 0,
    margin: 0,
    padding: 0,
    boxSizing: 'border-box'
  },
  imageSection: {
    width: `calc((100vh - 66px - 200px - 60px - 60px) * ${ASPECT_RATIO})`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    paddingLeft: '4px',
    minWidth: 0,
    boxSizing: 'border-box'
  },
  mapSection: {
    flex: 1,
    height: '100%',
    flexShrink: 0,
    margin: 0,
    padding: 0,
    minWidth: 0,
    boxSizing: 'border-box'
  },
  mapContainer: {
    margin: 0,
    padding: 0,
    boxSizing: 'border-box'
  },
  largeImageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 66px - 200px)',
    width: '100%',
    flexShrink: 0,
    margin: 0,
    padding: 0,
    boxSizing: 'border-box'
  },
  topOverlay: {
    height: '60px',
    backgroundColor: 'rgba(0, 0, 0, 1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
    flexShrink: '0',
    margin: '0',
    boxSizing: 'border-box'
  },
  imageWrapper: {
    position: 'relative',
    height: 'calc(100vh - 66px - 200px - 60px - 60px)',
    width: '100%',
    margin: '0',
    padding: '0',
    boxSizing: 'border-box',
    overflow: 'hidden',
    touchAction: 'pan-y',
    '& .main-slide': {
      textAlign: 'center',
      background: '#000',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      width: '100%',
      height: '100%',
      cursor: 'grab',
      userSelect: 'none',
      '&:active': {
        cursor: 'grabbing'
      }
    },
    '& .main-slide img': {
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center'
    }
  },
  largeImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  bottomOverlay: {
    height: '60px',
    backgroundColor: 'rgba(0, 0, 0, 1)',
    flexShrink: '0',
    margin: '0 !important',
    padding: '0 !important',
    boxSizing: 'border-box'
  },
  imageOverlay: {
    display: 'flex',
    gap: theme.spacing(1),
    margin: '0',
    padding: '0',
    boxSizing: 'border-box'
  },
  iconButton: {
    padding: theme.spacing(1)
  },
  actionIcon: {
    width: '24px',
    height: '24px'
  },
  thumbnailContainer: {
    height: '200px',
    width: '100%',
    flexShrink: '0',
    margin: '0',
    padding: '4px',
    boxSizing: 'border-box',
    position: 'relative',
    backgroundColor: '#f0f0f0',
    overflow: 'hidden'
  },
  thumbnail: {
    display: 'flex',
    flexDirection: 'column',
    width: `${THUMBNAIL_ITEM_WIDTH}px`,
    height: `${THUMBNAIL_ITEM_HEIGHT}px`,
    cursor: 'pointer',
    borderRadius: '4px',
    border: '3px solid transparent',
    margin: '0',
    padding: '0',
    boxSizing: 'border-box'
  },
  thumbnailSelected: {
    border: '2px solid red',
    borderRadius: '6px'
  },
  thumbnailImage: {
    width: '100%',
    height: '100px',
    objectFit: 'cover',
    borderRadius: '4px 4px 0 0'
  },
  thumbnailInfo: {
    backgroundColor: '#FFFFFF',
    color: '#000',
    borderRadius: '0 0 4px 4px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(0.5),
    gap: theme.spacing(1),
    height: '86px',
    boxSizing: 'border-box',
    fontSize: '12px',
    lineHeight: '16px',
    overflow: 'hidden'
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    flex: 1,
    maxWidth: '55%',
    minWidth: 0,
    overflow: 'hidden'
  },
  rightSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    maxWidth: '45%',
    minWidth: 0,
    overflow: 'hidden',
    paddingTop: '8px'
  },
  thumbnailInfoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    width: '100%',
    paddingBottom: '6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  thumbnailIcon: {
    width: '14px',
    height: '14px',
    flexShrink: 0
  },
  downloadButton: {
    padding: theme.spacing(0.5),
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  downloadIcon: {
    width: '16px',
    height: '16px'
  },
  typographyCaption: {
    fontSize: '12px',
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    maxWidth: '100%'
  },
  emptyBox: {
    position: 'absolute',
    top: '66px',
    left: props => (props.isSidebarOpen ? '384px' : '28px'),
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.background.dark
  },
  emptyContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  navButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.6)'
    }
  }
}));

Result.propTypes = {
  isShowResult: PropTypes.bool.isRequired,
  isSidebarOpen: PropTypes.bool.isRequired
};

function Result({ isShowResult, isSidebarOpen }) {
  const classes = useStyles({ isSidebarOpen });
  const [isOpenImage, setIsOpenImage] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [dataPhotoView, setDataPhotoView] = useState({});
  const thumbnailListRef = useRef(null);

  const listImages = useSelector(
    state => state.imageSlice?.listImage || []
  ).map(image => ({
    id: image.id,
    url: image.url || '/static/default-image.jpg',
    thumbnailUrl: image.url || '/static/default-image.jpg',
    file_name: image.file_name,
    created_at:
      image.created_at ||
      (image.device_time
        ? new Date(image.device_time * 1000).toLocaleString()
        : '--'),
    camera: `Camera ${image.camera_num || '--'}`,
    device: {
      vehicle: { license_plate: image.device.vehicle?.license_plate || '--' },
      location: image.location || '--',
      position: {
        lat: parseFloat(image.lat) || DEFAULT_CENTER[0],
        lng: parseFloat(image.lng) || DEFAULT_CENTER[1]
      },
      device_status: image.device?.status || '--',
      direction: 0
    }
  }));

  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (listImages.length > 0 && !selectedImage) {
      setSelectedImage(listImages[0]);
      setDataPhotoView(listImages[0]);
    }
  }, [listImages, selectedImage]);

  useEffect(() => {
    if (selectedImage) {
      setDataPhotoView(selectedImage);
    }
  }, [selectedImage]);

  const handleDisplayIcon = useCallback(vehicle => {
    const defaultIcon = L.icon({
      iconUrl: '/static/iconSvg/cars/car_lost_gsm.svg',
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });

    if (!vehicle) {
      return { icon: defaultIcon, rotation: 0 };
    }

    if (vehicle.debt === true) {
      return {
        icon: L.icon({
          iconUrl: '/static/iconSvg/motors/motor_expired.svg',
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        }),
        rotation: vehicle.direction || 0
      };
    }

    const iconVehicle = getIconStatus(
      vehicle.device_status,
      vehicle.vehicle_type || 'car'
    );
    if (
      !iconVehicle ||
      typeof iconVehicle !== 'string' ||
      !iconVehicle.endsWith('.svg')
    ) {
      return { icon: defaultIcon, rotation: 0 };
    }

    return {
      icon: L.icon({
        iconUrl: iconVehicle,
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      }),
      rotation: vehicle.direction || 0
    };
  }, []);

  const mapMarker = useCallback(() => {
    if (selectedImage?.device?.vehicle) {
      const { icon, rotation } = handleDisplayIcon(selectedImage.device);
      return {
        icon,
        rotation,
        license_plate: selectedImage.device.vehicle.license_plate
      };
    }
    return null;
  }, [selectedImage, handleDisplayIcon]);

  const mapCenter = useCallback(() => {
    if (selectedImage?.device?.position) {
      return [
        selectedImage.device.position.lat,
        selectedImage.device.position.lng
      ];
    }
    return DEFAULT_CENTER;
  }, [selectedImage]);

  const handleDownload = imageUrl => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'image.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleThumbnailClick = (image, index) => {
    setPhotoIndex(index);
    setSelectedImage(image);
    setDataPhotoView(image);
    if (thumbnailListRef.current) {
      thumbnailListRef.current.scrollToItem(index, 'start');
    }
  };

  const handlePrev = () => {
    const newIndex = Math.max(0, photoIndex - 1);
    setPhotoIndex(newIndex);
    setSelectedImage(listImages[newIndex]);
    setDataPhotoView(listImages[newIndex]);
    if (thumbnailListRef.current) {
      thumbnailListRef.current.scrollToItem(newIndex, 'start');
    }
  };

  const handleNext = () => {
    const newIndex = Math.min(listImages.length - 1, photoIndex + 1);
    setPhotoIndex(newIndex);
    setSelectedImage(listImages[newIndex]);
    setDataPhotoView(listImages[newIndex]);
    if (thumbnailListRef.current) {
      thumbnailListRef.current.scrollToItem(newIndex, 'start');
    }
  };

  const ThumbnailSlide = ({ index, style }) => {
    const image = listImages[index];
    return (
      <ThumbnailItem
        image={image}
        index={index}
        selectedImage={selectedImage}
        classes={classes}
        handleThumbnailClick={handleThumbnailClick}
        handleDownload={handleDownload}
        style={style}
      />
    );
  };

  useEffect(() => {
    if (thumbnailListRef.current && listImages.length > 0) {
      thumbnailListRef.current.scrollToItem(photoIndex, 'start');
    }
  }, [photoIndex, listImages]);

  const actionDetailsImgRef = index => {
    setPhotoIndex(index);
    setDataPhotoView(listImages[index] || {});
    setIsOpenImage(true);
  };

  return (
    <>
      {isShowResult && (
        <>
          {listImages.length === 0 ? (
            <Box className={classes.emptyBox}>
              <Box className={classes.emptyContent}>
                <Box>
                  <img src="/static/empty.png" />
                </Box>
                <Typography>
                  <h4>Danh sách trống</h4>
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box className={classes.container}>
              <Box className={classes.mainContent}>
                <Box className={classes.imageSection}>
                  <Box className={classes.largeImageContainer}>
                    <Box className={classes.topOverlay}>
                      <Box
                        style={{
                          display: 'flex',
                          gap: '15px',
                          alignItems: 'center',
                          paddingLeft: '20px'
                        }}
                      >
                        <Box style={{ cursor: 'pointer' }}>
                          <img
                            style={{ width: '24px' }}
                            src="/static/iconSvg/image-active-icon.svg"
                            alt="Image"
                          />
                        </Box>
                        <Box>
                          <AntTypography
                            style={{ color: '#ffffff', fontSize: '15px' }}
                          >
                            {moment(dataPhotoView.created_at * 1000).format(
                                          'HH:mm:ss DD/MM/YYYY'
                                        ) || '--'}
                          </AntTypography>
                        </Box>
                      </Box>
                      <Box className={classes.imageOverlay}>
                        <IconButton
                          onClick={() =>
                            handleDownload(selectedImage?.url || '')
                          }
                          className={classes.iconButton}
                        >
                          <img
                            src="/static/iconSvg/iconDownload.svg"
                            alt="Download Icon"
                            className={classes.actionIcon}
                          />
                        </IconButton>
                        <IconButton
                          onClick={() => actionDetailsImgRef(photoIndex)}
                          className={classes.iconButton}
                        >
                          <img
                            src="/static/iconSvg/iconMaximize.svg"
                            alt="Maximize Icon"
                            className={classes.actionIcon}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box className={classes.imageWrapper}>
                      {selectedImage ? (
                        <div className="main-slide">
                          <img
                            src={selectedImage.url}
                            alt={selectedImage.file_name}
                            className={classes.largeImage}
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <Box>Loading...</Box>
                      )}
                      <IconButton
                        className={classes.navButton}
                        onClick={handlePrev}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <img
                          src="/static/iconSvg/iconPrev.svg"
                          alt="Previous"
                          style={{ width: '14px', height: '14px' }}
                        />
                      </IconButton>
                      <IconButton
                        className={classes.navButton}
                        onClick={handleNext}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <img
                          src="/static/iconSvg/iconNext.svg"
                          alt="Next"
                          style={{ width: '14px', height: '14px' }}
                        />
                      </IconButton>
                    </Box>
                    <Box className={classes.bottomOverlay} />
                  </Box>
                </Box>
                <Box className={classes.mapSection}>
                  {selectedImage &&
                    selectedImage.device &&
                    selectedImage.device.position && (
                      <MapContainer
                        center={mapCenter()}
                        zoom={15}
                        maxZoom={22}
                        minZoom={6}
                        style={{ height: '100%', width: '100%' }}
                        zoomControl={false}
                        scrollWheelZoom={true}
                        zoomAnimation={true}
                        className={classes.mapContainer}
                      >
                        <TileLayer
                          url="https://{s}.google.com/vt/lyrs=m&hl=vi&x={x}&y={y}&z={z}"
                          subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                          maxZoom={22}
                        />
                        <MapUpdater
                          isSidebarOpen={isSidebarOpen}
                          center={mapCenter()}
                          marker={mapMarker()}
                          location={selectedImage.device.location}
                        />
                      </MapContainer>
                    )}
                </Box>
              </Box>
              <Box className={classes.thumbnailContainer}>
                <AutoSizer>
                  {({ height, width }) => (
                    <List
                      height={height}
                      width={width}
                      itemCount={listImages.length}
                      itemSize={THUMBNAIL_ITEM_WIDTH}
                      layout="horizontal"
                      itemData={listImages}
                      ref={thumbnailListRef}
                      initialScrollOffset={photoIndex * THUMBNAIL_ITEM_WIDTH}
                    >
                      {ThumbnailSlide}
                    </List>
                  )}
                </AutoSizer>
                <IconButton
                  className={classes.navButton}
                  onClick={handlePrev}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '32%',
                    transform: 'translateY(-50%)',
                    width: '36px',
                    height: '36px'
                  }}
                >
                  <img
                    src="/static/iconSvg/iconPrev.svg"
                    alt="Previous"
                    style={{ width: '14px', height: '14px' }}
                  />
                </IconButton>
                <IconButton
                  className={classes.navButton}
                  onClick={handleNext}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '32%',
                    transform: 'translateY(-50%)',
                    width: '36px',
                    height: '36px'
                  }}
                >
                  <img
                    src="/static/iconSvg/iconNext.svg"
                    alt="Next"
                    style={{ width: '14px', height: '14px' }}
                  />
                </IconButton>
              </Box>
            </Box>
          )}
        </>
      )}

      {isOpenImage && (
        <div>
          <Lightbox
            images={listImages}
            startIndex={photoIndex}
            onClose={() => setIsOpenImage(false)}
          />
          <Box
            style={{
              position: 'fixed',
              top: '48px',
              left: '30px',
              display: 'flex',
              width: '96%',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 99999
            }}
          >
            <Box style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <Box style={{ cursor: 'pointer' }}>
                <img src="/static/iconSvg/image-active-icon.svg" alt="Image" />
              </Box>
              <Box>
                <AntTypography style={{ color: '#ffffff' }}>
                  {dataPhotoView.created_at}
                </AntTypography>
              </Box>
            </Box>
            <Box style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <Box
                style={{ cursor: 'pointer' }}
                onClick={() => handleDownload(dataPhotoView.url)}
              >
                <img
                  style={{ width: '28px' }}
                  src="/static/iconSvg/downloadIcon.svg"
                  alt="Download"
                />
              </Box>
            </Box>
          </Box>
        </div>
      )}
    </>
  );
}

export default Result;
